import { Box, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';

import Avatar from './Avatar';

import { User } from 'services/help-center/queries/useUser/useUser.interface';
import { BOT_AUTHOR_ID, BOT_NAME } from 'services/help-center/constants';
import CollectiveIcon from 'assets/img/collective_icon_white.svg';

interface SenderHeaderProps {
    sender: Partial<User>;
    timestamp: string;
}

const ROLE_TO_BG_COLOR: Record<string, string> = {
    'end-user': 'primary',
    agent: 'secondary',
    admin: 'secondary',
    none: 'primary',
};

function formatDisplayName(name: string): string {
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 1) {
        return nameParts[0]; // single name, just return as-is
    }
    const firstName = nameParts[0];
    const lastInitial = nameParts[nameParts.length - 1]?.[0] || '';
    return `${firstName} ${lastInitial}.`;
}

function getSenderName(sender: Partial<User>, isBot: boolean): string {
    if (isBot) {
        return BOT_NAME;
    }

    const isEndUser = sender?.role === 'end-user';
    const preferredName = sender?.user_fields?.preferred_name;
    const rawName = sender?.name;

    if (isEndUser && preferredName) {
        return preferredName;
    }

    // If not an end-user or no preferred name is set, fall back to formatted version of full name.
    return rawName ? formatDisplayName(rawName) : 'Unknown';
}

function getAvatarBackgroundColor(role?: string): string {
    return ROLE_TO_BG_COLOR[role ?? 'none'] || ROLE_TO_BG_COLOR.none;
}

export default function SenderHeader({ sender, timestamp }: SenderHeaderProps): JSX.Element {
    const isBot = sender?.id === BOT_AUTHOR_ID;
    const senderName = getSenderName(sender, isBot);
    const formattedTimestamp = dayjs(timestamp).format('h:mm a');
    const avatarBgColor = getAvatarBackgroundColor(sender?.role);

    return (
        <Box sx={{ paddingBottom: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar name={senderName} color={avatarBgColor} src={isBot ? CollectiveIcon : ''} variant="medium" />
            <Typography variant="h6" component="span">
                {senderName}
            </Typography>
            <Tooltip title={dayjs(timestamp).format('MMMM D, YYYY h:mm a')} placement="top-start">
                <Typography variant="body2" component="span" color="cement.500" sx={{ marginLeft: 0.5 }}>
                    {formattedTimestamp}
                </Typography>
            </Tooltip>
        </Box>
    );
}
